import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseIcon, Clips, Picture, Stars } from '../../../assets/svgs/icons'
import styles from './styles.module.css'
import ModalComponent from '../../../components/ModalComponent'
import ModalGallery from '../../../components/ModalGallery'
import ModalAI from '../../../components/ModalAI'
import ModalProducts from '../../../components/ModalProducts'
import ModalImageBank from '../../../components/ModalImageBank'
import { notifyErrorCustom } from '../../../utils/notify'

export const ProductImageSelector = ({ isOpen, onClose, onSave, preselectedImages = [], maxImages, fixedPosition = false }) => {
  const { t } = useTranslation()
  const [images, setImages] = useState([])

  const inputFileRef =  useRef(null);
  useEffect(() => {
    if (isOpen && preselectedImages && preselectedImages.length > 0) {
      setImages(preselectedImages);
    } else if (isOpen && (!preselectedImages || preselectedImages.length === 0)) {
      setImages([]);
    }
  }, [isOpen, preselectedImages]);
  const [isModalGalleryOpen, setIsModalGalleryOpen] = useState(false)
  const [isModalAIOpen, setIsModalAIOpen] = useState(false)
  const [isModalProductsOpen, setIsModalProductsOpen] = useState(false)
  const [isModalImageBankOpen, setIsModalImageBankOpen] = useState(false)
  const [editingImageIndex, setEditingImageIndex] = useState(null)

  if (!isOpen) return null

  const isAtLimit = maxImages && images.length >= maxImages;

  const handleAddImages = (newImages, source) => {
    if (editingImageIndex !== null) {
      const updatedImages = [...images]
      updatedImages[editingImageIndex] = {
        ...newImages[0],
        source: source,
        id: images[editingImageIndex].id,
      }
      setImages(updatedImages)
      setEditingImageIndex(null)
    } else {
      const imagesWithSource = newImages.map((img, index) => ({
        ...img,
        source: source,
        id: img.id || `img-${Date.now()}-${index}`,
      }))
      if (maxImages) {
        const remaining = maxImages - images.length;
        setImages([...images, ...imagesWithSource.slice(0, Math.max(0, remaining))])
      } else {
        setImages([...images, ...imagesWithSource])
      }
    }
  }

  const handleOpenModal = (type) => {
    setEditingImageIndex(null);
  
    if (type === 'gallery'){
      if (inputFileRef.current) {
        inputFileRef.current.value = '';
      }
      inputFileRef.current.click();
    }
    if (type === 'imageBank') setIsModalImageBankOpen(true)
    if (type === 'ia') setIsModalAIOpen(true)
    if (type === 'products') setIsModalProductsOpen(true)
  }

  const handleEditImage = (index, source) => {
    setEditingImageIndex(index);

    if (source === 'gallery') {
      const handleUpdate = (e) => {
        onFileUpdate(e, index);
        inputFileRef.current.onchange = onFileChange;
        inputFileRef.current.value = '';
        setEditingImageIndex(null);
      };
  
      inputFileRef.current.onchange = handleUpdate;
      inputFileRef.current.click();
    }
    if (source === 'ia') setIsModalAIOpen(true);
    if (source === 'imageBank') setIsModalImageBankOpen(true);
    if (source === 'products') setIsModalProductsOpen(true);
  }

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index))
  }

  const handleSave = () => {
    if (onSave) {
      onSave(images)
    }
    onClose()
  }

  const calculateBase64Size = (base64String) => {
    if (!base64String) return 0
    const base64Data = base64String.includes(',') 
      ? base64String.split(',')[1] 
      : base64String
    const padding = (base64Data.match(/=/g) || []).length
    const sizeInBytes = (base64Data.length * 3) / 4 - padding
    return sizeInBytes
  }

  const getImageSize = (image) => {
    if (image.size) return `${(image.size / 1024 / 1024).toFixed(2)} MB`
    if (image.fileSize) return `${(image.fileSize / 1024 / 1024).toFixed(2)} MB`
    
    if (image.fileDataBase64) {
      const size = calculateBase64Size(image.fileDataBase64)
      if (size > 0) return `${(size / 1024 / 1024).toFixed(2)} MB`
    }
    if (image.base64) {
      const size = calculateBase64Size(image.base64)
      if (size > 0) return `${(size / 1024 / 1024).toFixed(2)} MB`
    }
    if (image.image && image.image.startsWith('data:')) {
      const size = calculateBase64Size(image.image)
      if (size > 0) return `${(size / 1024 / 1024).toFixed(2)} MB`
    }
    
    return t('chatsocket.product_image.size_na')
  }


  function onFileChange(e) {
    if (editingImageIndex !== null) return;

    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const filesToProcess = maxImages ? files.slice(0, Math.max(0, maxImages - images.length)) : files;

    filesToProcess.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        notifyErrorCustom(
          t('chatsocket.product_image.file_too_large'),
          t('chatsocket.product_image.file_too_large_title'),
          8000,
          { width: '480px', right: '160px'}
        );
        return;
      }

      const reader = new FileReader()
      reader.onload = function () {
        let fileDataBase64 = reader.result
        let fileDataReturn = {
          fileDataBase64,
          name: file.name,
          size: file.size,
          type: file.type,
          source: 'gallery',
          id: `img-${Date.now()}-${Math.random()}`,
        }
        setImages((current) => {
          if (maxImages && current.length >= maxImages) return current;
          return [...current, fileDataReturn];
        })
      }
      reader.readAsDataURL(file)
    })
  
    e.target.value = '';
  }

  function onFileUpdate(e, index) {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      notifyErrorCustom(
        t('chatsocket.product_image.file_too_large'),
        t('chatsocket.product_image.file_too_large_title'),
        8000,
        { width: '580px', right: '320px' }
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = function () {
      const fileDataReturn = {
        fileDataBase64: reader.result,
        name: file.name,
        size: file.size,
        type: file.type,
        source: 'gallery',
        id: images[index].id,
      };
    
      setImages(prevImages => {
        const newImages = [...prevImages];
        newImages[index] = fileDataReturn;
        return newImages;
      });
    };
  
    reader.readAsDataURL(file);
  }

 
  return (
    <>
      <div className={`${styles.container} ${fixedPosition ? styles.containerFixed : ''}`}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2 className={styles.title}>{t('chatsocket.product_image.modal_title')}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon color="#000" size="20" />
            </button>
          </div>

          <div className={styles.content}>
            <div className={styles.buttonsContainer}>
              <div className={styles.buttonsRow}>
                <button
                  className={`${styles.selectButton} ${isAtLimit ? styles.disabledButton : ''}`}
                  disabled={isAtLimit}
                  onClick={(e)=>{
                    e.preventDefault
                    e.stopPropagation
                  }}
                > 
                  <input ref={inputFileRef} 
                    disabled={isAtLimit}
                    className={styles.fileInput} 
                    type="file" onChange={onFileChange} />
                  <div className={styles.buttonIcon}>
                    <Clips width={24} height={24} />
                  </div>
                  <span className={styles.buttonText}>{t('chatsocket.product_image.gallery')}</span>
                </button>
                <button
                  className={`${styles.selectButton} ${isAtLimit ? styles.disabledButton : ''}`}
                  disabled={isAtLimit}
                  onClick={() => handleOpenModal('imageBank')}>
                  <div className={styles.buttonIcon}>
                    <Picture size={24} />
                  </div>
                  <span className={styles.buttonText}>{t('chatsocket.product_image.image_bank')}</span>
                </button>
                <button
                  className={`${styles.selectButton} ${isAtLimit ? styles.disabledButton : ''}`}
                  disabled={isAtLimit}
                  onClick={() => handleOpenModal('ia')}
                >
                  <div className={styles.buttonIcon}>
                    <Stars width={24} height={24} />
                  </div>
                  <span className={styles.buttonText}>{t('chatsocket.product_image.ai_image_bank')}</span>
                </button>
              </div>
            </div>

            <div className={styles.imagesList}>
              {images.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>{t('chatsocket.product_image.no_image_selected')}</p>
                </div>
              ) : (
                images.map((image, index) => (
                  <div key={image.id || index} className={styles.imageItem}>
                    <div className={styles.imagePreview}>
                      {image.fileDataBase64 || image.image ? (
                        <img
                          src={image.fileDataBase64 || image.image}
                          alt={`Imagem ${index + 1}`}
                          className={styles.previewImage}
                        />
                      ) : (
                        <div className={styles.placeholder}>P</div>
                      )}
                    </div>
                    <div className={styles.imageInfo}>
                      <span className={styles.imageSize}>{getImageSize(image)}</span>
                    </div>
                    <button
                      className={styles.changeButton}
                      onClick={() =>{handleEditImage(index, image.source)}
                      } 
                    >
                      {t('chatsocket.product_image.change')}
                    </button>
                    <button
                      className={styles.removeButton}
                      onClick={() => handleRemoveImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <button className={styles.saveButton} onClick={handleSave}>
            {t('chatsocket.product_image.save_images')}
          </button>
        </div>
      </div>

      <ModalComponent
        isOpen={isModalGalleryOpen}
        onClose={() => setIsModalGalleryOpen(false)}
        overlayStyle={{ zIndex: 2000 }}
      >
        <ModalGallery
          onClose={() => setIsModalGalleryOpen(false)}
          onSave={(newImages) => {
            handleAddImages(newImages, 'gallery')
            setIsModalGalleryOpen(false)
          }}
        />
      </ModalComponent>

      <ModalComponent
        isOpen={isModalAIOpen}
        onClose={() => setIsModalAIOpen(false)}
        overlayStyle={{ zIndex: 2000 }}
      >
        <ModalAI
          onClose={() => setIsModalAIOpen(false)}
          onSave={(newImages) => {
            handleAddImages(newImages, 'ia')
            setIsModalAIOpen(false)
          }}
        />
      </ModalComponent>

      <ModalComponent
        isOpen={isModalProductsOpen}
        onClose={() => setIsModalProductsOpen(false)}
        overlayStyle={{ zIndex: 2000 }}
      >
        <ModalProducts
          onClose={() => setIsModalProductsOpen(false)}
          onSave={(newImages) => {
            handleAddImages(newImages, 'products')
            setIsModalProductsOpen(false)
          }}
        />
      </ModalComponent>

      <ModalComponent
        isOpen={isModalImageBankOpen}
        onClose={() => setIsModalImageBankOpen(false)}
        overlayStyle={{ zIndex: 2000 }}
      >
        <ModalImageBank
          onClose={() => setIsModalImageBankOpen(false)}
          onSave={(newImages) => {
            handleAddImages(newImages, 'imageBank')
            setIsModalImageBankOpen(false)
          }}
        />
      </ModalComponent>
    </>
  )
}
